import {
  BASELINE_SECONDARY_PROVIDER_CODES,
  buildSecondaryPriceColumnType,
  DAILY_NAV_COLUMN_TYPES,
  HIDDEN_SECONDARY_PROVIDER_CODES,
  parseSecondaryPriceProviderCode,
} from 'pages/Intelligence/DailyNav/daily-nav.constants';
import { shortDate } from 'utilities';
import { DailyNavColumn, DailyNavTableRow } from '../../useDailyNavSheet.types';

type ColumnGenerator = (rows: DailyNavTableRow[]) => DailyNavColumn;

const toOptionalNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const isFundSecurityRow = (row: DailyNavTableRow) => row.kind === 'fund_security';

const buildColumn = (
  rows: DailyNavTableRow[],
  getValue: (row: DailyNavTableRow) => string | number | null
): DailyNavColumn =>
  rows.reduce(
    (accumulator, row) => ({ ...accumulator, [row.alias]: { value: getValue(row) } }),
    {} as DailyNavColumn
  );

// Company-level market data has no per-security counterpart; children stay blank.
const buildCompanyOnlyColumn = (
  rows: DailyNavTableRow[],
  getValue: (row: DailyNavTableRow) => string | number | null
): DailyNavColumn => buildColumn(rows, row => (isFundSecurityRow(row) ? null : getValue(row)));

// The bands, in the order they are rendered: what we last marked the company at,
// then the price the row reports today, then what the private market says now, then the public comps, then review state.
const PREVIOUS_VALUATION_COLUMNS: string[] = [
  DAILY_NAV_COLUMN_TYPES.VALUATION_DATE,
  DAILY_NAV_COLUMN_TYPES.PRICE_PER_SHARE,
];

const TODAY_COLUMNS: string[] = [DAILY_NAV_COLUMN_TYPES.TODAYS_PRICE];

// Secondary Data columns that follow the per-provider price columns: the mean
// across those providers, how far it sits from our mark, then the window deltas.
const SECONDARY_DELTA_COLUMNS: string[] = [
  DAILY_NAV_COLUMN_TYPES.SECONDARY_AVERAGE_PRICE,
  DAILY_NAV_COLUMN_TYPES.SECONDARY_CHANGE_FROM_MARK,
  DAILY_NAV_COLUMN_TYPES.SECONDARY_SHORT_WINDOW_DELTA,
  DAILY_NAV_COLUMN_TYPES.SECONDARY_LONG_WINDOW_DELTA,
  DAILY_NAV_COLUMN_TYPES.SECONDARY_SINCE_LAST_VALUATION_DELTA,
  DAILY_NAV_COLUMN_TYPES.CLOSED_TRANSACTIONS,
  DAILY_NAV_COLUMN_TYPES.NEWS,
];

const PUBLIC_COMP_COLUMNS: string[] = [
  DAILY_NAV_COLUMN_TYPES.SHORT_WINDOW_DELTA,
  DAILY_NAV_COLUMN_TYPES.LONG_WINDOW_DELTA,
  DAILY_NAV_COLUMN_TYPES.SINCE_LAST_VALUATION_DELTA,
];

const NAV_CONSIDERATION_COLUMNS: string[] = [DAILY_NAV_COLUMN_TYPES.STATUS];

// The baseline providers always get a column, plus any other provider_code
// discovered across the rows' secondary breakdowns — a company may map a subset,
// or none, and the section should not change shape because of it. Hidden
// providers are dropped. Sorted alphabetically for a stable column order
// regardless of API response ordering.
const getSecondaryProviderCodes = (rows: DailyNavTableRow[]): string[] => {
  const codes = new Set<string>(BASELINE_SECONDARY_PROVIDER_CODES);
  rows.forEach(row => {
    row.record?.secondary_breakdown?.providers?.forEach(provider => codes.add(provider.provider_code));
  });
  return Array.from(codes)
    .filter(code => !HIDDEN_SECONDARY_PROVIDER_CODES.has(code))
    .sort();
};

// Today's Price resolves its own allocation server-side, which may not be the
// pinned one, so a holding is matched by fund and security rather than by index.
// Null (blank) when that allocation does not price the holding, or while the
// row's review is pending.
const getTodaysPrice = (row: DailyNavTableRow): number | null => {
  const todaysPrice = row.record.todays_price;
  if (!todaysPrice) {
    return null;
  }
  if (!isFundSecurityRow(row)) {
    return toOptionalNumber(todaysPrice.common_share_value);
  }
  const holding = todaysPrice.investments.find(
    investment => investment.fund_id === row.fundId && investment.security_id === row.securityId
  );
  return toOptionalNumber(holding?.weighted_share_value);
};

const findProviderRow = (row: DailyNavTableRow, providerCode: string) =>
  row.record.secondary_breakdown?.providers.find(provider => provider.provider_code === providerCode);

// Builds the full column-type order for a render, threading the dynamic
// per-provider Secondary Data price columns in between the static bands.
export const buildDailyNavColumnTypeOrder = (rows: DailyNavTableRow[]): string[] => [
  ...PREVIOUS_VALUATION_COLUMNS,
  ...TODAY_COLUMNS,
  ...getSecondaryProviderCodes(rows).map(buildSecondaryPriceColumnType),
  ...SECONDARY_DELTA_COLUMNS,
  ...PUBLIC_COMP_COLUMNS,
  ...NAV_CONSIDERATION_COLUMNS,
];

const staticColumnGenerators: Record<string, ColumnGenerator> = {
  [DAILY_NAV_COLUMN_TYPES.VALUATION_DATE]: rows =>
    buildColumn(rows, row => shortDate(row.previousValuation?.measurement_date_value) ?? null),

  // Company row: first common on the allocation. Fund-security children: that
  // holding's weighted share value, which differs per security.
  [DAILY_NAV_COLUMN_TYPES.PRICE_PER_SHARE]: rows =>
    buildColumn(rows, row =>
      toOptionalNumber(
        isFundSecurityRow(row) ? row.weightedShareValue : row.previousValuation?.common_share_value
      )
    ),

  [DAILY_NAV_COLUMN_TYPES.TODAYS_PRICE]: rows => buildColumn(rows, getTodaysPrice),

  // Averaged window values from the record's inline breakdown, as decimal-string
  // fractions (e.g. "0.021"). Null when no comp had data.
  [DAILY_NAV_COLUMN_TYPES.SHORT_WINDOW_DELTA]: rows =>
    buildCompanyOnlyColumn(rows, row => toOptionalNumber(row.record.breakdown?.short_window?.average)),

  [DAILY_NAV_COLUMN_TYPES.LONG_WINDOW_DELTA]: rows =>
    buildCompanyOnlyColumn(rows, row => toOptionalNumber(row.record.breakdown?.long_window?.average)),

  [DAILY_NAV_COLUMN_TYPES.SINCE_LAST_VALUATION_DELTA]: rows =>
    buildCompanyOnlyColumn(rows, row => toOptionalNumber(row.record.breakdown?.since_last_valuation?.average)),

  // Mean of the per-provider prices, already averaged server-side so a provider
  // that reported nothing is skipped rather than counted as zero. Null when no
  // provider had a price at all.
  [DAILY_NAV_COLUMN_TYPES.SECONDARY_AVERAGE_PRICE]: rows =>
    buildCompanyOnlyColumn(rows, row => toOptionalNumber(row.record.secondary_breakdown?.price?.average)),

  // (average price / mark) - 1, as a decimal-string fraction. Computed server-side
  // rather than divided here: the mark is resolved from the same Looking Glass read
  // that anchors the windows, so the client would have to re-derive which valuation
  // counted — and would get it wrong on any historical date.
  [DAILY_NAV_COLUMN_TYPES.SECONDARY_CHANGE_FROM_MARK]: rows =>
    buildCompanyOnlyColumn(rows, row => toOptionalNumber(row.record.secondary_breakdown?.change_from_mark?.pct)),

  // Averaged window values from the secondary breakdown — the server has already
  // averaged the per-provider percent changes, excluding providers that couldn't
  // produce one, so nothing is recomputed here. As decimal-string fractions
  // (e.g. "0.021"); null when no provider had data for the window.
  [DAILY_NAV_COLUMN_TYPES.SECONDARY_SHORT_WINDOW_DELTA]: rows =>
    buildCompanyOnlyColumn(rows, row => toOptionalNumber(row.record.secondary_breakdown?.short_window?.average)),

  [DAILY_NAV_COLUMN_TYPES.SECONDARY_LONG_WINDOW_DELTA]: rows =>
    buildCompanyOnlyColumn(rows, row => toOptionalNumber(row.record.secondary_breakdown?.long_window?.average)),

  // The secondary side has always computed this window -- it is in the payload
  // and can breach its threshold -- but the grid never showed it. A breach here
  // lit the row's Review Price button with no number on screen to explain it.
  [DAILY_NAV_COLUMN_TYPES.SECONDARY_SINCE_LAST_VALUATION_DELTA]: rows =>
    buildCompanyOnlyColumn(rows, row =>
      toOptionalNumber(row.record.secondary_breakdown?.since_last_valuation?.average)
    ),

  // Value is unused — createParser renders ClosedTransactionsCell from the breakdown.
  [DAILY_NAV_COLUMN_TYPES.CLOSED_TRANSACTIONS]: rows => buildCompanyOnlyColumn(rows, () => null),

  // Value is unused — createParser renders NewsCell from the breakdown.
  [DAILY_NAV_COLUMN_TYPES.NEWS]: rows => buildCompanyOnlyColumn(rows, () => null),

  // Value is unused for this column — createParser renders a ReviewPriceCell component instead.
  [DAILY_NAV_COLUMN_TYPES.STATUS]: rows => buildColumn(rows, () => null),
};

// One generator per discovered provider, closing over its code so each column
// only ever reads that provider's own row. A company with no row for a given
// provider yields null here, which createParser renders blank rather than $0.00.
//
// Reads the price as reported. Nothing converts secondary prices — Alexandria
// owns that — so the band renders in whatever currency the providers used, which
// price.currency names.
const buildSecondaryPriceGenerator = (providerCode: string): ColumnGenerator => rows =>
  buildCompanyOnlyColumn(rows, row => toOptionalNumber(findProviderRow(row, providerCode)?.current_value));

const createColumns = (rows: DailyNavTableRow[]): DailyNavColumn[] =>
  buildDailyNavColumnTypeOrder(rows).map(columnType => {
    const providerCode = parseSecondaryPriceProviderCode(columnType);
    if (providerCode) {
      return buildSecondaryPriceGenerator(providerCode)(rows);
    }
    return staticColumnGenerators[columnType](rows);
  });

export default createColumns;
